import { ZodIssue, ZodType, ZodTypeDef } from 'zod';
import {
  MarketingLookupReqPayload,
  MarketingLookupResponse,
  MarketingLookupResponseSchema,
  ResolveExternalIdsReqPayload,
  ResolveExternalIdsResponse,
  ResolveExternalIdsResponseSchema,
} from './organizationServiceSchema';

export interface APIRequestOptions extends RequestInit {
  cacheEnabled?: boolean;
  bustCache?: boolean;
}

export interface APIClientConfig {
  baseUrl: string;
  defaultHeaders?: Record<string, string>;
  cache?: Map<string, Response>;
  newRelic?: {
    noticeError?: (error: Error, context: Record<string, any>) => void;
  };
  strictValidation?: boolean;
}

export class OrganizationServiceApiClient {
  public cache: Map<string, Response> = new Map();
  public readonly config: APIClientConfig;

  constructor(config: APIClientConfig) {
    const defaultConfig: APIClientConfig = {
      baseUrl: '',
      defaultHeaders: {},
      cache: new Map(),
      newRelic: {
        noticeError: (error: Error, context: Record<string, any>) => {
          console.error(
            'Error in OrganizationServiceApiClient: noticeError',
            error,
            context,
          );
        },
      },
      strictValidation: false,
    };
    this.config = { ...defaultConfig, ...config };
    this.cache = this.config.cache ?? new Map();
  }

  public getApiName(): string {
    return 'organization-service';
  }

  private async post<Body>(
    path: string,
    body: Body,
    options: APIRequestOptions = { cacheEnabled: false },
  ): Promise<Response> {
    const bodyString = JSON.stringify(body);
    const cacheKey = `${this.config.baseUrl}${path}`;

    if (options.bustCache) {
      this.bustCache(cacheKey);
    }

    if (this.shouldUseCache(cacheKey, options)) {
      const cached = this.cache.get(cacheKey);
      if (cached) {
        return cached.clone();
      }
    }

    const response = await fetch(`${this.config.baseUrl}${path}`, {
      method: 'POST',
      body: bodyString,
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });

    if (options.cacheEnabled && response.ok) {
      this.cache.set(cacheKey, response.clone());
    }
    return response;
  }

  private bustCache(cacheKey: string): void {
    this.cache.delete(cacheKey);
  }

  private shouldUseCache(
    cacheKey: string,
    options: APIRequestOptions,
  ): boolean {
    if (!options.cacheEnabled) {
      return false;
    }
    return this.cache.has(cacheKey);
  }

  private hasError(response: Response): boolean {
    return response.status >= 400;
  }

  /**
   * Converts the response to a JSON object that is defined by the passed in schema.
   */
  public async toJson<T>(
    response: Response,
    schema: ZodType<T, ZodTypeDef, any>,
  ): Promise<T> {
    const data = await response.json();
    // Validate the full response as-is
    const validated = schema.safeParse(data);

    if (!validated.success) {
      const error = new Error(`Invalid response for ${this.getApiName()}`);
      console.error(error);
      console.log(this.buildErrorMessage(validated.error.errors));

      if (this.config.strictValidation) {
        throw error;
      }
      return data as T;
    }
    return validated.data;
  }

  private buildErrorMessage(errors: ZodIssue[]): string[] {
    return errors.map(
      (error) => `Path: ${error.path.join('.')}, Message: ${error.message}`,
    );
  }

  /**
   * Requests marketing lookup data for multiple schools.
   * @param body - The marketing lookup request payload containing school IDs.
   * @param userAgent - Optional user agent string for the request.
   * @param options - Optional request options.
   * @returns The marketing lookup response containing organization marketing data.
   */
  async postMarketingLookup(
    body: MarketingLookupReqPayload,
    userAgent?: string,
    options: APIRequestOptions = { cacheEnabled: false },
  ): Promise<MarketingLookupResponse> {
    const headers: Record<string, string> = {};
    if (userAgent) {
      headers['User-Agent'] = userAgent;
    }

    const response = await this.post(`/v1/marketing/lookup`, body, {
      ...options,
      headers: { ...options.headers, ...headers },
    });

    if (this.hasError(response)) {
      console.error(
        `Failed to request marketing lookup for schools: ${body.schoolIds.join(', ')}`,
      );
    }

    const data = await this.toJson(response, MarketingLookupResponseSchema);
    return data;
  }

  /**
   * Resolve legacy EduDirect school IDs to organization UUIDs. The offerings
   * index keys on org UUIDs only, so the experience layer resolves legacy ids
   * here before querying offerings. Unknown ids are omitted from the response.
   * @param body - The request payload containing EduDirect school IDs.
   * @param options - Optional request options.
   * @returns Matching `{ edudirectId, organizationId }` pairs.
   */
  async resolveExternalIds(
    body: ResolveExternalIdsReqPayload,
    options: APIRequestOptions = { cacheEnabled: false },
  ): Promise<ResolveExternalIdsResponse> {
    const response = await this.post(
      `/v1/organizations/resolve-edudirect-ids`,
      body,
      options,
    );

    if (this.hasError(response)) {
      console.error(
        `Failed to resolve external ids: ${body.edudirectIds.join(', ')}`,
      );
    }

    return this.toJson(response, ResolveExternalIdsResponseSchema);
  }
}
